import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, Rate, Typography, Space, message, Tag, Empty } from 'antd';
import { CalendarOutlined, StarOutlined, InfoCircleOutlined } from '@ant-design/icons';
import api, { PLAYER_ACTIVE_TEAM_KEY } from '../../services/api';

const { Title, Text } = Typography;

const PlayerRatings = () => {
  const [games, setGames] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [ratingModalVisible, setRatingModalVisible] = useState(false);
  const [viewRatingsModalVisible, setViewRatingsModalVisible] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [players, setPlayers] = useState([]);
  const [ratings, setRatings] = useState({});
  const [viewRatings, setViewRatings] = useState([]);
  const [welcomeModal, setWelcomeModal] = useState(false);

  useEffect(() => {
    // Time ativo vem do TeamSwitcher (header do CommunityLayout), armazenado em
    // localStorage[PLAYER_ACTIVE_TEAM_KEY] — nao usamos mais o Select proprio (era redundante).
    const activeId = parseInt(localStorage.getItem(PLAYER_ACTIVE_TEAM_KEY) || '0', 10);
    if (activeId) {
      setSelectedTeam(activeId);
      loadGames(activeId);
    }

    const onTeamChange = (e) => {
      const cid = e.detail?.customer_id;
      if (cid) { setSelectedTeam(cid); loadGames(cid); }
    };
    window.addEventListener('active_customer_changed', onTeamChange);

    // Verificar se é o primeiro acesso às avaliações
    const hasSeenWelcome = localStorage.getItem('ratings_welcome_seen');
    if (!hasSeenWelcome) {
      setWelcomeModal(true);
    }

    return () => window.removeEventListener('active_customer_changed', onTeamChange);
  }, []);

  const loadGames = async (teamId) => {
    if (!teamId) {
      setGames([]);
      return;
    }
    
    try {
      const response = await api.get('/game-ratings/player/games', {
        headers: { Authorization: `Bearer ${localStorage.getItem('community_token')}` },
        params: { team_id: teamId }
      });
      
      setGames(response.data || []);
    } catch (error) {
      message.error('Erro ao carregar jogos');
      setGames([]);
    }
  };

  const openRatingModal = async (game) => {
    try {
      // Buscar jogadores do jogo
      const playersResponse = await api.get(`/game-ratings/game/${game.id}/players`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('community_token')}` }
      });
      
      // Buscar avaliações existentes
      const ratingsResponse = await api.get(`/game-ratings/player/ratings/${game.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('community_token')}` }
      });
      
      // Verificar se já avaliou todos
      const totalPlayers = playersResponse.data.length;
      const ratedCount = ratingsResponse.data.length;
      
      if (totalPlayers > 0 && ratedCount >= totalPlayers) {
        // Já avaliou todos, abrir modal de visualização
        openViewRatingsModal(game);
        return;
      }
      
      setSelectedGame(game);
      setRatingModalVisible(true);
      setPlayers(playersResponse.data || []);
      
      // Converter para formato do estado
      const existingRatings = {};
      ratingsResponse.data.forEach(rating => {
        const player = playersResponse.data.find(p => p.name === rating.player_name);
        if (player) {
          existingRatings[player.id] = rating.rating;
        }
      });
      
      setRatings(existingRatings);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      message.error('Erro ao carregar jogadores');
      setPlayers([]);
      setRatings({});
    }
  };

  const checkGameStatus = (game) => {
    // Usar o campo can_rate do backend
    if (game.can_rate === false) {
      return { canRate: false, reason: 'Prazo expirado (24h)' };
    }
    
    return { canRate: true, reason: '' };
  };

  const openViewRatingsModal = async (game) => {
    setSelectedGame(game);
    setViewRatingsModalVisible(true);
    
    try {
      const response = await api.get(`/game-ratings/player/ratings/${game.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('community_token')}` }
      });
      setViewRatings(response.data);
    } catch (error) {
      message.error('Erro ao carregar avaliações');
    }
  };

  const handleRating = async (playerId, value) => {
    try {
      await api.post('/game-ratings/player/rate', {
        game_id: selectedGame.id,
        player_id: playerId,
        rating: value
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('community_token')}` }
      });
      
      setRatings(prev => ({
        ...prev,
        [playerId]: value
      }));
      
      message.success(`Avaliação salva: ${value} estrelas`);
    } catch (error) {
      message.error('Erro ao salvar avaliação');
    }
  };

  const handleWelcomeClose = () => {
    localStorage.setItem('ratings_welcome_seen', 'true');
    setWelcomeModal(false);
  };



  return (
    <div style={{ padding: window.innerWidth < 768 ? '8px' : '20px' }}>
      <Title 
        level={window.innerWidth < 768 ? 3 : 2}
        style={{ fontSize: window.innerWidth < 768 ? 18 : 24 }}
      >
        {window.innerWidth < 768 ? 'Avaliações' : 'Minhas Avaliações'}
      </Title>
      
      {/* Dropdown de time removido — use o TeamSwitcher no header pra trocar de time */}

      {games.length === 0 && selectedTeam && (
        <Empty description="Nenhum jogo encontrado para este time" />
      )}

      <div style={{ 
        display: 'grid', 
        gap: window.innerWidth < 768 ? 8 : 16, 
        gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))'
      }}>
        {games.map(game => {
          const gameStatus = checkGameStatus(game);
          
          return (
            <Card
              key={game.id}
              size={window.innerWidth < 768 ? 'small' : 'default'}
              title={
                <Space size={window.innerWidth < 768 ? 4 : 8}>
                  <CalendarOutlined style={{ fontSize: window.innerWidth < 768 ? 12 : 14 }} />
                  <span style={{ fontSize: window.innerWidth < 768 ? 12 : 14 }}>
                    {game.date_of_game || 'Data não informada'}
                  </span>
                </Space>
              }
              extra={
                <Tag 
                  color={game.participated ? 'green' : 'orange'}
                  style={{ fontSize: window.innerWidth < 768 ? 10 : 12 }}
                >
                  {window.innerWidth < 768 ? (
                    game.participated ? 'Jogou' : 'Não jogou'
                  ) : (
                    game.participated ? 'Participou' : 'Não participou'
                  )}
                </Tag>
              }
              actions={[
                !gameStatus.canRate ? (
                  <Button 
                    disabled
                    size={window.innerWidth < 768 ? 'small' : 'default'}
                  >
                    {window.innerWidth < 768 ? 'Expirado' : gameStatus.reason}
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    icon={<StarOutlined />}
                    size={window.innerWidth < 768 ? 'small' : 'default'}
                    onClick={() => openRatingModal(game)}
                  >
                    Avaliar
                  </Button>
                )
              ]}
            >
              <Text 
                strong 
                style={{ fontSize: window.innerWidth < 768 ? 13 : 14 }}
              >
                {game.team_name}
              </Text>
              <br />
              <Text 
                type="secondary"
                style={{ fontSize: window.innerWidth < 768 ? 11 : 12 }}
              >
                {window.innerWidth < 768 ? (
                  !gameStatus.canRate ? 'Expirado' : 'Disponível'
                ) : (
                  !gameStatus.canRate ? gameStatus.reason : 'Disponível para avaliação'
                )}
              </Text>
            </Card>
          );
        })}
      </div>

      {/* Modal de Avaliação */}
      <Modal
        title="Avaliar Jogadores"
        open={ratingModalVisible}
        onCancel={() => setRatingModalVisible(false)}
        footer={[
          <Button 
            key="close" 
            size={window.innerWidth < 768 ? 'small' : 'default'}
            onClick={() => setRatingModalVisible(false)}
          >
            Fechar
          </Button>
        ]}
        width={window.innerWidth < 768 ? '95%' : 600}
        style={{ top: window.innerWidth < 768 ? 20 : undefined }}
      >
        <div style={{ 
          maxHeight: window.innerWidth < 768 ? 300 : 400, 
          overflowY: 'auto' 
        }}>
          {players.map(player => (
            <Card 
              key={player.id} 
              size="small" 
              style={{ marginBottom: window.innerWidth < 768 ? 6 : 10 }}
            >
              <Space 
                style={{ width: '100%', justifyContent: 'space-between' }}
                direction={window.innerWidth < 768 ? 'vertical' : 'horizontal'}
                align={window.innerWidth < 768 ? 'start' : 'center'}
              >
                <div>
                  <Text 
                    strong 
                    style={{ fontSize: window.innerWidth < 768 ? 13 : 14 }}
                  >
                    {player.name}
                  </Text>
                  <br />
                  <Text 
                    type="secondary"
                    style={{ fontSize: window.innerWidth < 768 ? 11 : 12 }}
                  >
                    {player.cellphone_number}
                  </Text>
                </div>
                <Rate
                  value={ratings[player.id] || 0}
                  onChange={(value) => handleRating(player.id, value)}
                  allowClear
                  style={{ 
                    fontSize: window.innerWidth < 768 ? 14 : 20,
                    display: 'flex',
                    gap: window.innerWidth < 768 ? '2px' : '4px'
                  }}
                />
              </Space>
            </Card>
          ))}
        </div>
      </Modal>

      {/* Modal de Visualização */}
      <Modal
        title="Suas Avaliações"
        open={viewRatingsModalVisible}
        onCancel={() => setViewRatingsModalVisible(false)}
        footer={null}
        width={window.innerWidth < 768 ? '95%' : 500}
        style={{ top: window.innerWidth < 768 ? 20 : undefined }}
      >
        <div style={{ 
          maxHeight: window.innerWidth < 768 ? 300 : 400, 
          overflowY: 'auto' 
        }}>
          {viewRatings.map((rating, index) => (
            <Card 
              key={index} 
              size="small" 
              style={{ marginBottom: window.innerWidth < 768 ? 6 : 10 }}
            >
              <Space 
                style={{ width: '100%', justifyContent: 'space-between' }}
                direction={window.innerWidth < 768 ? 'vertical' : 'horizontal'}
                align={window.innerWidth < 768 ? 'start' : 'center'}
              >
                <Text 
                  strong 
                  style={{ fontSize: window.innerWidth < 768 ? 13 : 14 }}
                >
                  {rating.player_name}
                </Text>
                <Rate 
                  disabled 
                  value={rating.rating} 
                  style={{ 
                    fontSize: window.innerWidth < 768 ? 14 : 20,
                    display: 'flex',
                    gap: window.innerWidth < 768 ? '2px' : '4px'
                  }}
                />
              </Space>
              <Text 
                type="secondary" 
                style={{ fontSize: window.innerWidth < 768 ? 10 : 12 }}
              >
                Avaliado em: {rating.created_at || 'Data não informada'}
              </Text>
            </Card>
          ))}
        </div>
      </Modal>
      
      {/* Modal de Boas-vindas ao Sistema de Avaliações */}
      <Modal
        title={<><InfoCircleOutlined style={{ color: '#1890ff' }} /> Sistema de Avaliações Futezap!</>}
        open={welcomeModal}
        onCancel={handleWelcomeClose}
        footer={[
          <Button key="start" type="primary" onClick={handleWelcomeClose}>
            Começar a Avaliar
          </Button>
        ]}
        width={600}
      >
        <div style={{ padding: '16px 0' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <StarOutlined style={{ fontSize: 48, color: '#1890ff' }} />
          </div>
          
          <Typography.Paragraph style={{ fontSize: 16, textAlign: 'center', marginBottom: 24 }}>
            O <strong>Sistema de Avaliações</strong> permite que você avalie outros jogadores após as partidas, construindo reputação na comunidade.
          </Typography.Paragraph>
          
          <div style={{ marginBottom: 16 }}>
            <Typography.Title level={5}>
              <StarOutlined style={{ color: '#52c41a' }} /> Como Funciona:
            </Typography.Title>
            <ul style={{ paddingLeft: 20 }}>
              <li>Após cada jogo, a partir de <strong>2h</strong> depois do fim, você tem <strong>24 horas</strong> para avaliar os outros jogadores</li>
              <li>Avalie de <strong>1 a 5 estrelas</strong> baseado no desempenho e fair play</li>
              <li>Suas avaliações são <strong>anônimas</strong> e ajudam a comunidade</li>
              <li>Jogadores bem avaliados ganham <strong>mais credibilidade</strong></li>
              <li>Você pode ver o <strong>histórico</strong> das suas avaliações</li>
            </ul>
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <Typography.Title level={5}>
              <CalendarOutlined style={{ color: '#faad14' }} /> Regras Importantes:
            </Typography.Title>
            <ul style={{ paddingLeft: 20 }}>
              <li><strong>Prazo de 24h</strong> - Após esse período, não é mais possível avaliar</li>
              <li><strong>Uma avaliação por jogador</strong> - Você pode alterar até o prazo expirar</li>
              <li><strong>Seja justo</strong> - Avalie baseado no desempenho real</li>
              <li><strong>Respeite o fair play</strong> - Considere atitude e comportamento</li>
            </ul>
          </div>
          
          <div style={{ 
            backgroundColor: '#f6ffed', 
            border: '1px solid #b7eb8f', 
            borderRadius: 6, 
            padding: 16, 
            textAlign: 'center' 
          }}>
            <Typography.Text strong style={{ color: '#389e0d' }}>
              ⭐ Dica: Avalie com critério! Suas avaliações ajudam a melhorar a comunidade!
            </Typography.Text>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PlayerRatings;